import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { getDomain } from 'tldts'
import type {
  ApplicationSnapshot,
  DashboardSnapshot,
  DomainSource,
  HealthIncident,
  HealthState,
  HostTelemetryPoint,
  HistoryPoint,
  InfrastructureSnapshot,
  ProductMetricSnapshot,
  TechnologyCategory,
  TechnologyConfidence,
  TechnologySignal,
  TechnologySource,
} from '../shared/types'
import { availableExternalMetric, declaresExternalAudience, productStatsAreFresh, type TractionScore } from '../shared/product-metrics'
import {
  availability,
  clockTime,
  compactLatency,
  fullDate,
  relativeTime,
  shortDate,
  stateCopy,
} from './format'
import {
  AlertIcon,
  BellIcon,
  CheckIcon,
  ChevronIcon,
  ClockIcon,
  CloseIcon,
  ExternalIcon,
  ServerIcon,
  TrendIcon,
} from './icons'

const DOMAIN_SOURCE_LABEL: Record<DomainSource, string> = {
  configured: 'Configured',
  caddy: 'Caddy',
  netlify: 'Netlify',
  spaceship: 'Spaceship',
}

const TECHNOLOGY_CATEGORY_LABEL: Record<TechnologyCategory, string> = {
  framework: 'Frameworks',
  language: 'Languages',
  runtime: 'Runtime',
  database: 'Data',
  hosting: 'Hosting',
  dns: 'DNS',
  registrar: 'Registrar',
  edge: 'Edge',
  build: 'Build',
  service: 'Services',
}

const TECHNOLOGY_SOURCE_LABEL: Record<TechnologySource, string> = {
  configured: 'Declared',
  caddy: 'Caddy',
  netlify: 'Netlify',
  spaceship: 'Spaceship',
  vps: 'VPS',
  detected: 'Detected',
}

const TECHNOLOGY_CONFIDENCE_LABEL: Record<TechnologyConfidence, string> = {
  declared: 'Declared',
  high: 'Verified',
  medium: 'Inferred',
  low: 'Possible',
}

const TECHNOLOGY_CATEGORY_ORDER: TechnologyCategory[] = [
  'framework',
  'language',
  'runtime',
  'database',
  'hosting',
  'dns',
  'registrar',
  'edge',
  'build',
  'service',
]

const TECHNOLOGY_CONFIDENCE_WEIGHT: Record<TechnologyConfidence, number> = {
  declared: 4,
  high: 3,
  medium: 2,
  low: 1,
}

function sortedTechnologies(technologies: TechnologySignal[]) {
  return [...technologies].sort(
    (left, right) =>
      TECHNOLOGY_CATEGORY_ORDER.indexOf(left.category) -
        TECHNOLOGY_CATEGORY_ORDER.indexOf(right.category) ||
      TECHNOLOGY_CONFIDENCE_WEIGHT[right.confidence] -
        TECHNOLOGY_CONFIDENCE_WEIGHT[left.confidence] ||
      left.name.localeCompare(right.name),
  )
}

function featuredTechnologies(technologies: TechnologySignal[], limit = 4) {
  const sorted = sortedTechnologies(technologies)
  const featured: TechnologySignal[] = []
  for (const category of [
    'framework',
    'language',
    'database',
    'hosting',
    'runtime',
    'dns',
    'registrar',
    'build',
    'service',
    'edge',
  ] satisfies TechnologyCategory[]) {
    const technology = sorted.find(
      (candidate) =>
        candidate.category === category &&
        !featured.includes(candidate),
    )
    if (technology) featured.push(technology)
    if (featured.length === limit) return featured
  }
  return featured
}

function technologyTitle(technology: TechnologySignal) {
  const sources = technology.sources
    .map((source) => TECHNOLOGY_SOURCE_LABEL[source])
    .join(' + ')
  return [
    `${TECHNOLOGY_CATEGORY_LABEL[technology.category]} · ${TECHNOLOGY_CONFIDENCE_LABEL[technology.confidence]} · ${sources}`,
    technology.evidence,
  ]
    .filter(Boolean)
    .join('\n')
}

export function StatusDot({
  state,
  pulse = false,
}: {
  state: HealthState
  pulse?: boolean
}) {
  return <span className={`status-dot status-${state} ${pulse ? 'pulse' : ''}`} aria-hidden="true" />
}

export function StatusPill({
  state,
  compact = false,
}: {
  state: HealthState
  compact?: boolean
}) {
  return (
    <span className={`status-pill status-${state}`}>
      <StatusDot state={state} />
      {compact ? stateCopy[state].short : stateCopy[state].short}
    </span>
  )
}

function HistoryStrip({ points }: { points: HistoryPoint[] }) {
  const recent = points.slice(-36)
  const slots: Array<HistoryPoint | null> = [
    ...Array(Math.max(0, 36 - recent.length)).fill(null),
    ...recent,
  ]
  return (
    <div className="history-strip" aria-label={`${recent.length} recent health observations`}>
      {slots.map((point, index) => (
        <span
          key={`${point?.observedAt ?? 'empty'}-${index}`}
          className={point ? `history-tick status-${point.status}` : 'history-tick empty'}
          title={point ? `${fullDate(point.observedAt)}: ${stateCopy[point.status].short}` : 'No data'}
        />
      ))}
    </div>
  )
}

function Sparkline({ points }: { points: HistoryPoint[] }) {
  const values = points.slice(-24).map((point) => point.durationMs)
  if (values.length < 2) return <div className="sparkline-empty">Building baseline</div>
  const maximum = Math.max(...values, 1)
  const minimum = Math.min(...values)
  const spread = Math.max(maximum - minimum, 1)
  const line = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * 100
      const y = 25 - ((value - minimum) / spread) * 20
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')
  return (
    <svg className="sparkline" viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={line} />
    </svg>
  )
}

export function FleetHero({ snapshot }: { snapshot: DashboardSnapshot }) {
  const summary = applicationFleetMetrics(snapshot)
  const healthyRatio = summary.applications
    ? summary.operational / summary.applications
    : 0
  const circumference = 2 * Math.PI * 50
  const offset = circumference * (1 - healthyRatio)

  return (
    <section className={`fleet-hero status-panel-${summary.status}`}>
      <div className="hero-copy">
        <div className="eyebrow">
          <StatusDot state={summary.status} pulse={summary.status === 'operational'} />
          Live fleet status
        </div>
        <h1>{stateCopy[summary.status].label}</h1>
        <p>{stateCopy[summary.status].description}</p>
        <div className="hero-timestamp">
          <ClockIcon />
          Checked {relativeTime(summary.observedAt)} · {clockTime(summary.observedAt)}
        </div>
      </div>
      <div className="fleet-ring" aria-label={`${summary.operational} of ${summary.applications} monitored systems operational`}>
        <svg viewBox="0 0 120 120" aria-hidden="true">
          <circle className="ring-track" cx="60" cy="60" r="50" />
          <circle
            className={`ring-value status-${summary.status}`}
            cx="60"
            cy="60"
            r="50"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="ring-label">
          <strong>{summary.operational}</strong>
          <span>of {summary.applications}</span>
        </div>
      </div>
    </section>
  )
}

export function MetricRail({ snapshot }: { snapshot: DashboardSnapshot }) {
  const summary = applicationFleetMetrics(snapshot)
  const metrics = [
    {
      label: '24h availability',
      value: availability(summary.availability24h),
      note: summary.availability24h === null ? 'Baseline pending' : 'Across applications',
    },
    {
      label: 'Active incidents',
      value: String(summary.activeIncidents),
      note: summary.activeIncidents ? 'Needs attention' : 'Nothing open',
      alert: summary.activeIncidents > 0,
    },
    {
      label: 'P95 response',
      value: compactLatency(summary.p95LatencyMs),
      note: `${summary.checks} component checks`,
    },
    {
      label: 'Fleet scan',
      value: compactLatency(summary.durationMs),
      note: snapshot.fresh
        ? 'Just completed'
        : `Background cycle ${relativeTime(summary.observedAt)}`,
    },
  ]
  return (
    <section className="metric-rail" aria-label="Fleet metrics">
      {metrics.map((metric) => (
        <div className={`metric ${metric.alert ? 'metric-alert' : ''}`} key={metric.label}>
          <span>{metric.label}</span>
          <strong>{metric.value}</strong>
          <small>{metric.note}</small>
        </div>
      ))}
    </section>
  )
}

function compactNumber(value: number) {
  return new Intl.NumberFormat('en-US', {
    notation: value >= 1_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value)
}

export function formatProductMetric(metric: ProductMetricSnapshot) {
  if (metric.status === 'suppressed') return 'Suppressed'
  if (metric.status === 'unavailable') return 'Unavailable'
  if (metric.status === 'not-applicable') return 'Not tracked'
  if (metric.value === undefined) return 'Unavailable'
  if (metric.unit === 'percent') return `${metric.value.toLocaleString()}%`
  if (metric.unit === 'currency-minor') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(metric.value / 100)
  }
  if (metric.unit === 'seconds') {
    const hours = metric.value / 3_600
    return hours >= 1 ? `${compactNumber(hours)}h` : `${compactNumber(metric.value)}s`
  }
  return compactNumber(metric.value)
}

export function ProductMetricRail({
  snapshot,
  scores,
}: {
  snapshot: DashboardSnapshot
  scores: Map<string, TractionScore>
}) {
  const applications = snapshot.applications.filter(
    (application) => application.application.kind !== 'infrastructure',
  )
  const aggregate = (
    semantic: ProductMetricSnapshot['semantic'],
    period: ProductMetricSnapshot['period'],
  ) => applications.reduce(
    (result, application) => {
      const metric = availableExternalMetric(application, semantic, period)
      return metric?.value === undefined
        ? result
        : { value: result.value + metric.value, sources: result.sources + 1 }
    },
    { value: 0, sources: 0 },
  )
  const active = aggregate('active-users', '7d')
  const newcomers = aggregate('new-users', '7d')
  const coreActions = aggregate('core-action', '7d')
  const growing = applications.filter(
    (application) =>
      (availableExternalMetric(application, 'active-users', '7d')?.change7d?.percent ?? 0) > 0,
  ).length
  const instrumented = applications.filter(
    (application) => productStatsAreFresh(application.productMetrics) && declaresExternalAudience(application.productMetrics?.audience),
  ).length
  const ranked = [...scores.values()].filter((score) => score.score !== null).length
  const pendingNote = instrumented
    ? 'No comparable metric reported'
    : 'External audience exclusions unconfirmed'
  const metrics = [
    {
      label: 'Active users',
      value: active.sources ? compactNumber(active.value) : '—',
      note: active.sources ? `Meaningful activity · 7d · ${active.sources} sources` : pendingNote,
    },
    {
      label: 'New users',
      value: newcomers.sources ? compactNumber(newcomers.value) : '—',
      note: newcomers.sources ? `New identities · 7d · ${newcomers.sources} sources` : pendingNote,
    },
    {
      label: 'Core actions',
      value: coreActions.sources ? compactNumber(coreActions.value) : '—',
      note: coreActions.sources ? `Primary outcomes · 7d · ${coreActions.sources} sources` : pendingNote,
    },
    {
      label: 'Products growing',
      value: instrumented ? String(growing) : '—',
      note: instrumented
        ? `${instrumented} live sources · ${ranked} ranked`
        : pendingNote,
    },
  ]
  return (
    <section className="metric-rail" aria-label="Portfolio traction metrics">
      {metrics.map((metric) => (
        <div className="metric" key={metric.label}>
          <span>{metric.label}</span>
          <strong>{metric.value}</strong>
          <small>{metric.note}</small>
        </div>
      ))}
    </section>
  )
}

function applicationFleetMetrics(snapshot: DashboardSnapshot) {
  const applications = snapshot.applications.filter(
    (application) => application.application.kind !== 'infrastructure',
  )
  const stateWeight: Record<HealthState, number> = {
    operational: 0,
    maintenance: 1,
    unknown: 2,
    degraded: 3,
    outage: 4,
  }
  const status = applications.reduce<HealthState>(
    (worst, application) =>
      stateWeight[application.status] > stateWeight[worst]
        ? application.status
        : worst,
    'operational',
  )
  const latencies = applications
    .flatMap((application) =>
      application.components
        .map((component) => component.latencyMs)
        .filter((value): value is number => value !== undefined),
    )
    .sort((left, right) => left - right)
  const availabilityValues = applications
    .map((application) => application.availability24h)
    .filter((value): value is number => value !== null)
  return {
    ...snapshot.summary,
    status,
    applications: applications.length,
    operational: applications.filter(
      (application) => application.status === 'operational',
    ).length,
    activeIncidents: applications.reduce(
      (sum, application) => sum + application.incidents.length,
      0,
    ),
    checks: applications.reduce(
      (sum, application) => sum + application.components.length,
      0,
    ),
    p95LatencyMs: latencies.length
      ? latencies[Math.min(latencies.length - 1, Math.ceil(latencies.length * 0.95) - 1)]
      : null,
    availability24h: availabilityValues.length
      ? Math.round(
          (availabilityValues.reduce((sum, value) => sum + value, 0) /
            availabilityValues.length) *
            100,
        ) / 100
      : null,
  }
}

function formatBytes(value: number, rate = false) {
  if (!Number.isFinite(value) || value <= 0) return rate ? '0 B/s' : '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const unit = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1000)))
  const amount = value / 1000 ** unit
  return `${amount >= 10 || unit === 0 ? amount.toFixed(0) : amount.toFixed(1)} ${units[unit]}${rate ? '/s' : ''}`
}

function machineUptime(bootedAt: string) {
  const days = Math.max(0, Math.floor((Date.now() - Date.parse(bootedAt)) / 86_400_000))
  if (days) return `${days}d uptime`
  const hours = Math.max(0, Math.floor((Date.now() - Date.parse(bootedAt)) / 3_600_000))
  return `${hours}h uptime`
}

function metricState(value: number, warning: number, critical: number): HealthState {
  if (value >= critical) return 'outage'
  if (value >= warning) return 'degraded'
  return 'operational'
}

function ResourceMetric({
  label,
  value,
  display,
  note,
  warning,
  critical,
  maximum = 100,
  status,
}: {
  label: string
  value: number
  display: string
  note: string
  warning: number
  critical: number
  maximum?: number
  status?: HealthState
}) {
  const state = status ?? metricState(value, warning, critical)
  return (
    <div className={`resource-metric status-resource-${state}`}>
      <div>
        <span>{label}</span>
        <StatusDot state={state} />
      </div>
      <strong>{display}</strong>
      <small>{note}</small>
      <div className="resource-track" aria-hidden="true">
        <i style={{ width: `${Math.min(100, Math.max(0, (value / maximum) * 100))}%` }} />
      </div>
    </div>
  )
}

type ChartSeries = {
  label: string
  color: string
  value: (point: HostTelemetryPoint) => number
}

function TelemetryChart({
  title,
  note,
  points,
  events,
  series,
  maximum,
  formatter = (value) => `${value.toFixed(0)}%`,
}: {
  title: string
  note: string
  points: HostTelemetryPoint[]
  events: InfrastructureSnapshot['events']
  series: ChartSeries[]
  maximum?: number
  formatter?: (value: number) => string
}) {
  if (points.length < 2) {
    return (
      <div className="telemetry-chart chart-awaiting">
        <header>
          <div>
            <h3>{title}</h3>
            <p>{note}</p>
          </div>
        </header>
        <div><span className="preview-orbit" />Building a trustworthy baseline</div>
      </div>
    )
  }
  const allValues = points.flatMap((point) => series.map((item) => item.value(point)))
  const peak = maximum ?? Math.max(1, ...allValues) * 1.15
  const startedAt = Date.parse(points[0].observedAt)
  const endedAt = Date.parse(points.at(-1)!.observedAt)
  const duration = Math.max(1, endedAt - startedAt)
  const width = 640
  const height = 178
  const top = 12
  const bottom = 156
  const chartHeight = bottom - top
  const pointLine = (item: ChartSeries) =>
    points
      .map((point, index) => {
        const x = (index / (points.length - 1)) * width
        const y = bottom - Math.min(1, item.value(point) / peak) * chartHeight
        return `${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(' ')
  const markers = events.filter((event) => {
    const time = Date.parse(event.occurredAt)
    return time >= startedAt && time <= endedAt
  })
  const latest = points.at(-1)!

  return (
    <div className="telemetry-chart">
      <header>
        <div>
          <h3>{title}</h3>
          <p>{note}</p>
        </div>
        <div className="chart-legend">
          {series.map((item) => (
            <span key={item.label}>
              <i style={{ background: item.color }} />
              {item.label} <strong>{formatter(item.value(latest))}</strong>
            </span>
          ))}
        </div>
      </header>
      <div className="chart-canvas">
        <span className="chart-peak">{formatter(peak)}</span>
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
          <path className="chart-grid-line" d={`M0 ${top}H${width}M0 ${(top + bottom) / 2}H${width}M0 ${bottom}H${width}`} />
          {markers.map((event) => {
            const x = ((Date.parse(event.occurredAt) - startedAt) / duration) * width
            return (
              <line
                key={event.id}
                className={`event-marker severity-${event.severity}`}
                x1={x}
                x2={x}
                y1={top}
                y2={bottom}
              />
            )
          })}
          {series.map((item) => (
            <polyline
              key={item.label}
              className="telemetry-line"
              points={pointLine(item)}
              style={{ stroke: item.color }}
            />
          ))}
        </svg>
        <div className="chart-axis">
          <span>{relativeTime(points[0].observedAt)}</span>
          <span>Now</span>
        </div>
      </div>
    </div>
  )
}

export function InfrastructurePanel({
  snapshot,
  range,
  onRangeChange,
  onOpenDetails,
}: {
  snapshot?: InfrastructureSnapshot
  range: InfrastructureSnapshot['range']
  onRangeChange: (range: InfrastructureSnapshot['range']) => void
  onOpenDetails: () => void
}) {
  const ranges: Array<{ id: InfrastructureSnapshot['range']; label: string }> = [
    { id: '1h', label: '1H' },
    { id: '6h', label: '6H' },
    { id: '24h', label: '24H' },
    { id: '7d', label: '7D' },
    { id: '30d', label: '30D' },
  ]
  const current = snapshot?.current
  const machine = snapshot?.machine
  const thresholds = snapshot?.thresholds
  const pressure = current
    ? Math.max(
        current.pressure.memoryFullPercent ?? 0,
        current.pressure.ioFullPercent ?? 0,
      )
    : 0
  const loadPerCore =
    current && machine ? current.load5 / Math.max(1, machine.cpuCores) : 0
  const throughput = (point: HostTelemetryPoint) =>
    point.networkRxBytesPerSecond + point.networkTxBytesPerSecond
  const diskThroughput = (point: HostTelemetryPoint) =>
    point.diskReadBytesPerSecond + point.diskWriteBytesPerSecond

  return (
    <section className={`infrastructure-section status-panel-${snapshot?.status ?? 'unknown'}`}>
      <header className="infrastructure-head">
        <div className="infrastructure-identity">
          <span className="machine-icon"><ServerIcon /></span>
          <div>
            <span className="section-kicker">Production infrastructure</span>
            <h2>SASID Production VPS</h2>
            <p>
              {machine
                ? `${machine.hostname} · ${machine.operatingSystem} · ${machine.architecture} · ${machine.cpuCores} vCPU · ${formatBytes(machine.totalMemoryBytes)} RAM`
                : 'Establishing a private signal from the physical host…'}
            </p>
          </div>
        </div>
        <div className="infrastructure-actions">
          <button
            className="machine-detail-button"
            onClick={onOpenDetails}
            aria-label="Open VPS health checks"
          >
            <span>Checks</span> <ChevronIcon />
          </button>
        </div>
      </header>

      {current && machine && thresholds ? (
        <>
          <div className="machine-facts">
            <span><StatusDot state={snapshot?.status ?? current.status} pulse={!snapshot?.stale} />{snapshot?.stale ? 'Telemetry stale' : 'Live host telemetry'}</span>
            <span>{machineUptime(machine.bootedAt)}</span>
            <span>{machine.kernel}</span>
            <span>{current.processCount} processes</span>
            <span>Sampled {relativeTime(current.observedAt)}</span>
          </div>

          <div className="resource-grid">
            <ResourceMetric
              label="CPU"
              value={current.cpuAverage1mPercent}
              display={`${current.cpuAverage1mPercent.toFixed(1)}%`}
              note={`${current.cpuPercent.toFixed(1)}% now · ${machine.cpuCores} cores`}
              warning={thresholds.cpuWarningPercent}
              critical={thresholds.cpuCriticalPercent}
            />
            <ResourceMetric
              label="Memory"
              value={current.memoryUsedPercent}
              display={`${current.memoryUsedPercent.toFixed(1)}%`}
              note={`${formatBytes(current.memoryAvailableBytes)} available`}
              warning={thresholds.memoryWarningPercent}
              critical={thresholds.memoryCriticalPercent}
            />
            <ResourceMetric
              label="Root disk"
              value={current.diskUsedPercent}
              display={`${current.diskUsedPercent.toFixed(1)}%`}
              note={`${formatBytes(current.diskFreeBytes)} free`}
              warning={thresholds.diskWarningPercent}
              critical={thresholds.diskCriticalPercent}
            />
            <ResourceMetric
              label="5m load / core"
              value={loadPerCore}
              display={loadPerCore.toFixed(2)}
              note={`${current.load1.toFixed(2)} · ${current.load5.toFixed(2)} · ${current.load15.toFixed(2)}`}
              warning={thresholds.loadWarningPerCore}
              critical={thresholds.loadCriticalPerCore}
              maximum={thresholds.loadCriticalPerCore}
            />
            <ResourceMetric
              label="Swap"
              status={snapshot?.componentStatuses?.swap ?? 'unknown'}
              value={current.swapUsedPercent}
              display={`${current.swapUsedPercent.toFixed(1)}%`}
              note={`${formatBytes(current.swapUsedBytes)} occupied`}
              warning={thresholds.swapWarningPercent}
              critical={thresholds.swapCriticalPercent}
            />
            <ResourceMetric
              label="Full pressure stalls"
              status={snapshot?.componentStatuses?.pressure ?? 'unknown'}
              value={pressure}
              display={`${pressure.toFixed(1)}%`}
              note={`MEM ${current.pressure.memoryFullPercent?.toFixed(1) ?? 'unavailable'} · I/O ${current.pressure.ioFullPercent?.toFixed(1) ?? 'unavailable'}`}
              warning={thresholds.pressureWarningPercent}
              critical={thresholds.pressureCriticalPercent}
              maximum={thresholds.pressureCriticalPercent}
            />
          </div>

          <section className="operator-briefing">
            <header>
              <TrendIcon />
              <div>
                <span>Operator briefing</span>
                <small>Prioritized from current values, recent peaks, trends, and incidents</small>
              </div>
            </header>
            <div className="operator-insight-list">
              {(snapshot.insights ?? []).map((insight) => (
                <article className={`operator-insight insight-${insight.severity}`} key={insight.id}>
                  <StatusDot
                    state={
                      insight.severity === 'critical'
                        ? 'outage'
                        : insight.severity === 'warning'
                          ? 'degraded'
                          : 'operational'
                    }
                  />
                  <div>
                    <div className="operator-insight-title">
                      {insight.scope && <span>{insight.scope}</span>}
                      <strong>{insight.title}</strong>
                    </div>
                    <p>{insight.detail}</p>
                    {insight.evidence && (
                      <small className="insight-evidence">{insight.evidence}</small>
                    )}
                    {insight.recommendation && (
                      <div className="insight-recommendation">
                        <b>Recommended</b>
                        <span>{insight.recommendation}</span>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <details className="machine-disclosure">
            <summary>
              <span className="disclosure-icon"><TrendIcon /></span>
              <span className="disclosure-copy">
                <strong>History & incident correlation</strong>
                <small>Charts, throughput, Linux pressure, and application event overlays</small>
              </span>
              <span className="disclosure-meta">
                {range.toUpperCase()} · {snapshot.events.length} event{snapshot.events.length === 1 ? '' : 's'}
              </span>
              <ChevronIcon />
            </summary>
            <div className="machine-disclosure-content">
              <div className="history-toolbar">
                <div>
                  <span>Machine history window</span>
                  <small>{snapshot.history.length} chart points in this view</small>
                </div>
                <div className="range-tabs" aria-label="Machine history range">
                  {ranges.map((item) => (
                    <button
                      key={item.id}
                      className={range === item.id ? 'active' : ''}
                      onClick={() => onRangeChange(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="telemetry-grid">
                <TelemetryChart
                  title="Compute & memory"
                  note="Resource demand aligned to incident markers"
                  points={snapshot.history}
                  events={snapshot.events}
                  maximum={100}
                  series={[
                    { label: 'CPU', color: '#8ce8b5', value: (point) => point.cpuAverage1mPercent },
                    { label: 'Memory', color: '#91c9f7', value: (point) => point.memoryUsedPercent },
                  ]}
                />
                <TelemetryChart
                  title="Storage & swap"
                  note="Capacity trends and virtual-memory occupancy"
                  points={snapshot.history}
                  events={snapshot.events}
                  maximum={100}
                  series={[
                    { label: 'Disk', color: '#f0ca78', value: (point) => point.diskUsedPercent },
                    { label: 'Swap', color: '#b8b0ff', value: (point) => point.swapUsedPercent },
                  ]}
                />
                <TelemetryChart
                  title="Host throughput"
                  note="Primary network interface and physical disk activity"
                  points={snapshot.history}
                  events={snapshot.events}
                  formatter={(value) => formatBytes(value, true)}
                  series={[
                    { label: 'Network', color: '#91c9f7', value: throughput },
                    { label: 'Disk I/O', color: '#f3b98a', value: diskThroughput },
                  ]}
                />
                <TelemetryChart
                  title="Linux pressure"
                  note="Time workloads spent stalled on constrained resources"
                  points={snapshot.history}
                  events={snapshot.events}
                  series={[
                    { label: 'CPU', color: '#8ce8b5', value: (point) => point.pressure.cpuPercent },
                    { label: 'Memory', color: '#91c9f7', value: (point) => point.pressure.memoryPercent },
                    { label: 'I/O', color: '#ff8d80', value: (point) => point.pressure.ioPercent },
                  ]}
                />
              </div>

              <section className="correlation-timeline">
                <header>
                  <span>Incident correlation</span>
                  <small>{snapshot.events.length ? `${snapshot.events.length} events in view` : 'No events in view'}</small>
                </header>
                {snapshot.events.length ? (
                  <div>
                    {snapshot.events.slice(0, 6).map((event) => {
                      const content = (
                        <>
                          <StatusDot
                            state={
                              event.severity === 'critical'
                                ? 'outage'
                                : event.severity === 'warning'
                                  ? 'degraded'
                                  : 'operational'
                            }
                          />
                          <div>
                            <strong>{event.title}</strong>
                            <p>{event.detail}</p>
                            <time>{fullDate(event.occurredAt)}</time>
                          </div>
                        </>
                      )
                      return event.applicationId ? (
                        <a
                          key={event.id}
                          href={`?app=${encodeURIComponent(event.applicationId)}${
                            event.incidentId
                              ? `&incident=${encodeURIComponent(event.incidentId)}`
                              : ''
                          }`}
                        >
                          {content}
                        </a>
                      ) : (
                        <article key={event.id}>{content}</article>
                      )
                    })}
                  </div>
                ) : (
                  <div className="correlation-empty">
                    <StatusDot state="operational" />
                    No application incidents overlap this machine-history window.
                  </div>
                )}
              </section>
            </div>
          </details>
        </>
      ) : (
        <div className="infrastructure-loading">
          <span className="button-spinner" />
          <div>
            <strong>Waiting for the host agent</strong>
            <p>The dashboard remains available while the first machine baseline is collected.</p>
          </div>
        </div>
      )}
    </section>
  )
}

export function ApplicationCard({
  application,
  history,
  lens,
  traction,
  onOpen,
}: {
  application: ApplicationSnapshot
  history: HistoryPoint[]
  lens: 'health' | 'traction'
  traction?: TractionScore
  onOpen: () => void
}) {
  const down = application.components.filter(
    (component) => component.status === 'outage' || component.status === 'degraded',
  ).length
  const latencyValues = application.components
    .map((component) => component.latencyMs)
    .filter((value): value is number => value !== undefined)
  const latency = latencyValues.length ? Math.max(...latencyValues) : null
  const previews = application.previews ?? []
  const preview =
    previews.find((item) => item.frames.length > 0) ??
    previews[0]
  const latestFrame = preview?.frames[0]
  const homepage = application.application.homepage
  const domainInventory =
    application.application.domainInventory ??
    (application.application.domains ?? []).map((hostname) => ({
      hostname,
      sources: ['configured' as const],
      role: undefined,
    }))
  const primaryDomain =
    domainInventory.find((domain) => domain.role === 'primary')?.hostname ??
    domainInventory[0]?.hostname
  const displayUrl =
    homepage?.replace(/^https?:\/\//, '').replace(/\/$/, '') ?? primaryDomain
  const sources = [
    ...new Set(domainInventory.flatMap((domain) => domain.sources)),
  ].filter((source) => source !== 'configured')
  const sourceCopy =
    sources.map((source) => DOMAIN_SOURCE_LABEL[source]).join(' · ') ||
    application.application.tags?.slice(0, 2).join(' · ')
  const technologies = application.application.technologies ?? []
  const featured = featuredTechnologies(technologies)
  const deployment = application.application.deployment
  const primaryProductMetric =
    application.productMetrics?.metrics.find(
      (metric) =>
        metric.status === 'available' &&
        metric.value !== undefined &&
        metric.semantic === 'core-action' &&
        metric.period === 'all-time',
    ) ??
    application.productMetrics?.metrics.find(
      (metric) =>
        metric.status === 'available' &&
        metric.value !== undefined &&
        metric.category === 'content',
    )
  const activeUsers = availableExternalMetric(application, 'active-users', '7d')

  return (
    <article
      className={`application-card status-card-${application.status}`}
    >
      <button
        className="card-open-trigger"
        type="button"
        onClick={onOpen}
        aria-label={`Open ${application.application.name} health details and visual history`}
      />
      <div className="card-head">
        <ApplicationIcon
          applicationId={application.application.id}
          name={application.application.name}
          src={
            application.application.homepage
              ? `/api/v1/icons/${encodeURIComponent(application.application.id)}`
              : undefined
          }
          accent={application.application.accent}
          fit={application.application.iconFit}
          position={application.application.iconPosition}
        />
        <div className="app-title">
          <h3>{application.application.name}</h3>
          <span>{sourceCopy}</span>
        </div>
        <StatusPill state={application.status} compact />
      </div>

      {displayUrl &&
        (homepage ? (
          <a
            className="app-domain"
            href={homepage}
            target="_blank"
            rel="noreferrer"
            title={`Open ${homepage}`}
          >
            <ExternalIcon />
            <span>{displayUrl}</span>
            {domainInventory.length > 1 && <small>{domainInventory.length} domains</small>}
          </a>
        ) : (
          <div className="app-domain domain-unlinked" title={displayUrl}>
            <span>{displayUrl}</span>
            {domainInventory.length > 1 && <small>{domainInventory.length} domains</small>}
          </div>
        ))}

      {lens === 'traction' && (
        <div className={`product-signal-row source-${application.productMetrics?.sourceStatus ?? 'none'}`}>
          {productStatsAreFresh(application.productMetrics) ? (
            <>
              <div>
                <span>{primaryProductMetric?.name ?? 'Total users'}</span>
                <strong>
                  {primaryProductMetric
                    ? formatProductMetric(primaryProductMetric)
                    : availableExternalMetric(application, 'total-users', 'all-time')
                      ? formatProductMetric(availableExternalMetric(application, 'total-users', 'all-time')!)
                      : 'Collecting'}
                </strong>
              </div>
              <div>
                <span>Active · 7d</span>
                <strong>{activeUsers ? formatProductMetric(activeUsers) : '—'}</strong>
              </div>
              <div>
                <span>Traction</span>
                <strong>{traction?.score ?? (traction?.status === 'audience-unconfirmed' ? 'Unconfirmed' : 'Baseline')}</strong>
                {activeUsers?.change7d && (
                  <small>
                    {activeUsers.change7d.percent === null
                      ? `${activeUsers.change7d.absolute >= 0 ? '+' : ''}${compactNumber(activeUsers.change7d.absolute)}`
                      : `${activeUsers.change7d.percent >= 0 ? 'Up' : 'Down'} ${Math.abs(activeUsers.change7d.percent)}%`}
                  </small>
                )}
              </div>
            </>
          ) : (
            <div className="product-signal-empty">
              <span>Product telemetry</span>
              <strong>
                {!application.productMetrics
                  ? 'Not instrumented'
                  : application.productMetrics.sourceStatus === 'unavailable'
                    ? 'Unavailable'
                    : application.productMetrics.sourceStatus === 'invalid'
                      ? 'Contract needs attention'
                      : 'Last-known data is stale'}
              </strong>
            </div>
          )}
        </div>
      )}

      {lens === 'health' && featured.length > 0 && (
        <div className="technology-strip" aria-label="Technology overview">
          {featured.map((technology) => (
            <span
              className={`technology-chip confidence-${technology.confidence}`}
              key={`${technology.category}-${technology.name}`}
              title={technologyTitle(technology)}
            >
              {technology.name}
              {technology.confidence === 'low' && ' ?'}
            </span>
          ))}
          {technologies.length > featured.length && (
            <span className="technology-more">
              +{technologies.length - featured.length}
            </span>
          )}
        </div>
      )}

      {lens === 'health' && (deployment?.first || deployment?.latest) && (
        <div className="deployment-strip" aria-label="Deployment timeline">
          {deployment.first && (
            <span title={`${fullDate(deployment.first.at)}\n${deployment.first.evidence ?? ''}`}>
              <small>First deployed</small>
              <strong>{shortDate(deployment.first.at)}</strong>
            </span>
          )}
          {deployment.latest && (
            <span title={`${fullDate(deployment.latest.at)}\n${deployment.latest.evidence ?? ''}`}>
              <small>Latest deployment</small>
              <strong>{relativeTime(deployment.latest.at)}</strong>
            </span>
          )}
        </div>
      )}

      <div className={`card-preview ${latestFrame ? '' : 'preview-empty'}`}>
        {latestFrame ? (
          <PreviewImage
            src={latestFrame.imageUrl}
            alt={`${application.application.name} rendered at ${fullDate(latestFrame.capturedAt)}`}
          />
        ) : (
          <div>
            <span className="preview-orbit" />
            <strong>{homepage ? 'Awaiting visual check' : 'Registration tracked'}</strong>
          </div>
        )}
        <div className="preview-caption">
          <span>
            <StatusDot state={preview?.status ?? (homepage ? 'unknown' : 'operational')} />
            {preview?.name ?? (homepage ? 'Live preview' : 'Domain portfolio')}
          </span>
          <time title={latestFrame ? fullDate(latestFrame.capturedAt) : undefined}>
            {latestFrame
              ? relativeTime(latestFrame.capturedAt)
              : homepage
                ? 'Pending'
                : 'No website expected'}
          </time>
        </div>
      </div>

      <div className="card-message">
        {down > 0 ? (
          <AlertIcon />
        ) : (
          <CheckIcon />
        )}
        <span>{application.summary}</span>
      </div>

      <HistoryStrip points={history} />

      <div className="card-foot">
        <div>
          <span>24h</span>
          <strong>{availability(application.availability24h)}</strong>
        </div>
        <div>
          <span>Slowest</span>
          <strong>{compactLatency(latency)}</strong>
        </div>
        <div className="mini-chart">
          <Sparkline points={history} />
        </div>
        <ChevronIcon />
      </div>
    </article>
  )
}

/**
 * A screenshot that is never allowed to go blank.
 *
 * Relay recaptures hourly and every capture publishes a new frame
 * id, so the polled snapshot hands this page a different URL for the same
 * screenshot. Assigning that straight to `src` empties the element until the
 * new bytes decode, which made the whole grid flash to nothing on every cycle.
 *
 * So the next frame is decoded off-screen first and only swapped in once it is
 * ready, and a frame that fails outright is simply not swapped in: the last
 * image that worked stays on screen. The only time a reader sees no screenshot
 * is when there has never been one.
 */
export function PreviewImage({
  src,
  alt,
  fallback = null,
}: {
  src: string
  alt: string
  /** Shown only when no frame has ever loaded, never in place of a good one. */
  fallback?: ReactNode
}) {
  const [shown, setShown] = useState(src)
  const [broken, setBroken] = useState(false)
  const shownRef = useRef(src)

  useEffect(() => {
    if (src === shownRef.current) return
    let cancelled = false
    const loader = new Image()
    loader.src = src
    const ready = () => {
      if (cancelled) return
      shownRef.current = src
      setShown(src)
      setBroken(false)
    }
    // A rejected decode means the frame was pruned or never written. Keeping
    // the previous frame on screen is always better than an empty card.
    if (typeof loader.decode === 'function') {
      void loader.decode().then(ready, () => undefined)
    } else {
      loader.onload = ready
    }
    return () => {
      cancelled = true
    }
  }, [src])

  if (broken) return <>{fallback}</>
  return (
    <img
      src={shown}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
    />
  )
}

export function ApplicationIcon({
  applicationId,
  name,
  src,
  accent,
  fit = 'contain',
  position = 'center',
  className = '',
}: {
  applicationId: string
  name: string
  src?: string
  accent?: string
  fit?: 'contain' | 'cover'
  position?: 'left' | 'center' | 'right'
  className?: string
}) {
  const [failed, setFailed] = useState(false)
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')

  return (
    <div
      className={`app-monogram app-identity-icon icon-fit-${fit} ${className}`.trim()}
      style={{
        '--accent': accent ?? '#9de1c1',
        '--icon-fit': fit,
        '--icon-position': position,
      } as CSSProperties}
      data-application-id={applicationId}
    >
      <span aria-hidden={Boolean(src) && !failed}>{initials}</span>
      {src && !failed && (
        <img
          src={src}
          alt={`${name} icon`}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  )
}

interface ScreenshotViewerTarget {
  previewId: string
  frameId: string
}

function ProductMetricsPanel({ application }: { application: ApplicationSnapshot }) {
  const report = application.productMetrics
  if (!report) {
    return (
      <section className="product-metrics-panel source-none" aria-label="Product telemetry">
        <header>
          <div>
            <span>Product intelligence</span>
            <h3>Not instrumented yet</h3>
          </div>
          <small>Add a SASID Product Stats v1 endpoint</small>
        </header>
      </section>
    )
  }
  const primaryOrder: ProductMetricSnapshot['semantic'][] = [
    'core-action',
    'conversion',
    'active-users',
    'online-users',
    'total-users',
  ]
  const available = report.metrics.filter((metric) => metric.status === 'available')
  const primary = [...available]
    .sort(
      (left, right) => {
        const leftIndex = primaryOrder.indexOf(left.semantic)
        const rightIndex = primaryOrder.indexOf(right.semantic)
        return (leftIndex < 0 ? 99 : leftIndex) - (rightIndex < 0 ? 99 : rightIndex)
      },
    )
    .slice(0, 4)
  const primaryIds = new Set(primary.map((metric) => metric.id))
  const secondary = report.metrics.filter((metric) => !primaryIds.has(metric.id))
  const current = productStatsAreFresh(report)
  const displayStatus = report.sourceStatus === 'fresh' && !current ? 'stale' : report.sourceStatus
  const statusLabel = current && report.message?.startsWith('Declared')
    ? 'Configured catalog'
    : ({
    fresh: 'Live source',
    stale: 'Last-known data',
    invalid: 'Invalid contract',
    unavailable: 'Source unavailable',
  }[displayStatus])

  return (
    <section
      className={`product-metrics-panel source-${displayStatus}`}
      aria-label="Product telemetry"
    >
      <header>
        <div>
          <span>Product intelligence</span>
          <h3>Audience & traction</h3>
        </div>
        <div className="product-source-state">
          <StatusDot state={current ? 'operational' : 'unknown'} />
          <span>{statusLabel}</span>
          {report.observedAt && (
            <time title={fullDate(report.observedAt)}>{relativeTime(report.observedAt)}</time>
          )}
        </div>
      </header>
      {primary.length > 0 ? (
        <div className="product-kpi-grid">
          {primary.map((metric) => (
            <article key={metric.id} title={metric.description}>
              <span>{metric.name}</span>
              <strong>{formatProductMetric(metric)}</strong>
              <small>
                {metric.period === 'instant' ? 'Right now' : metric.period}
                {metric.change7d?.percent !== undefined && (
                  <> · {metric.change7d.percent === null
                    ? `${metric.change7d.absolute >= 0 ? '+' : ''}${compactNumber(metric.change7d.absolute)}`
                    : `${metric.change7d.percent >= 0 ? 'Up' : 'Down'} ${Math.abs(metric.change7d.percent)}% vs 7d ago`}</>
                )}
              </small>
            </article>
          ))}
        </div>
      ) : (
        <p className="product-metrics-message">
          {report.message ?? 'No product metrics are currently available.'}
        </p>
      )}
      {secondary.length > 0 && (
        <details className="product-activity-details">
          <summary>
            <span>All product metrics</span>
            <strong>{secondary.length} more</strong>
          </summary>
          <div className="product-activity-list">
            {secondary.map((metric) => (
              <div key={metric.id} title={metric.description}>
                <span>{metric.name}<small>{metric.period}</small></span>
                <strong>{formatProductMetric(metric)}</strong>
              </div>
            ))}
          </div>
        </details>
      )}
      {report.message && primary.length > 0 && <p className="product-metrics-message">{report.message}</p>}
    </section>
  )
}

export function EmptyState({ filter }: { filter: string }) {
  return (
    <div className="empty-state">
      <div className="empty-orbit" />
      <h3>No matching systems</h3>
      <p>Nothing in this fleet matches “{filter}”. Try another name, tag, or state.</p>
    </div>
  )
}

export function DetailSheet({
  application,
  history,
  focusedComponentId,
  focusedIncident,
  onClose,
}: {
  application: ApplicationSnapshot
  history: HistoryPoint[]
  focusedComponentId?: string
  focusedIncident?: HealthIncident
  onClose: () => void
}) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const viewerCloseRef = useRef<HTMLButtonElement>(null)
  const [viewer, setViewer] = useState<ScreenshotViewerTarget>()
  const focusedComponent = application.components.find(
    (component) => component.id === focusedComponentId,
  )
  const domainInventory =
    application.application.domainInventory ??
    (application.application.domains ?? []).map((hostname) => ({
      hostname,
      sources: ['configured' as const],
      role: undefined,
    }))
  const apexDomainInventory = domainInventory.filter(
    (domain) =>
      getDomain(domain.hostname, { allowPrivateDomains: false }) ===
      domain.hostname.toLowerCase(),
  )
  const primaryDomainInventory =
    apexDomainInventory.length > 0
      ? apexDomainInventory
      : domainInventory.filter((domain) => domain.role === 'primary').slice(0, 1)
  const featuredDomainInventory =
    primaryDomainInventory.length > 0
      ? primaryDomainInventory
      : domainInventory.slice(0, 1)
  const featuredHostnames = new Set(
    featuredDomainInventory.map((domain) => domain.hostname),
  )
  const secondaryDomainInventory = domainInventory.filter(
    (domain) => !featuredHostnames.has(domain.hostname),
  )
  const technologies = sortedTechnologies(
    application.application.technologies ?? [],
  )
  const activePreview = application.previews.find(
    (preview) => preview.id === viewer?.previewId,
  )
  const activeFrameIndex =
    activePreview?.frames.findIndex((frame) => frame.id === viewer?.frameId) ?? -1
  const activeFrame =
    activeFrameIndex >= 0 ? activePreview?.frames[activeFrameIndex] : undefined

  function moveViewer(delta: number) {
    if (!activePreview || activeFrameIndex < 0) return
    const next = activePreview.frames[activeFrameIndex + delta]
    if (next) setViewer({ previewId: activePreview.id, frameId: next.id })
  }

  useEffect(() => {
    if (viewer) viewerCloseRef.current?.focus()
    else closeRef.current?.focus()
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (viewer) setViewer(undefined)
        else onClose()
      }
      if (viewer && event.key === 'ArrowLeft') moveViewer(-1)
      if (viewer && event.key === 'ArrowRight') moveViewer(1)
    }
    document.body.classList.add('sheet-open')
    window.addEventListener('keydown', listener)
    return () => {
      document.body.classList.remove('sheet-open')
      window.removeEventListener('keydown', listener)
    }
  }, [activeFrameIndex, activePreview, onClose, viewer])

  return (
    <>
      <div className="sheet-layer" role="presentation" onMouseDown={onClose}>
        <section
          className="detail-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="detail-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
        <div className="sheet-grab" />
        <header className="sheet-head">
          <div>
            <div className="eyebrow">
              <StatusDot state={application.status} />
              {application.application.environment}
            </div>
            <h2 id="detail-title">{application.application.name}</h2>
            <p>{application.application.description}</p>
          </div>
          <button className="icon-button" ref={closeRef} onClick={onClose} aria-label="Close details">
            <CloseIcon />
          </button>
        </header>

        {(focusedComponent || focusedIncident) && (
          <section
            className={`linked-error-card status-card-${
              focusedIncident?.status === 'resolved'
                ? 'operational'
                : focusedComponent?.status ?? 'outage'
            }`}
            aria-label="Notification error detail"
            data-testid="notification-detail"
          >
            <div>
              <StatusDot
                state={
                  focusedIncident?.status === 'resolved'
                    ? 'operational'
                    : focusedComponent?.status ?? 'outage'
                }
              />
              <span>
                {focusedIncident?.status === 'resolved'
                  ? 'Resolved alert'
                  : 'Opened from alert'}
              </span>
            </div>
            <h3>{focusedComponent?.name ?? focusedIncident?.title}</h3>
            <p>
              {focusedIncident?.message ??
                focusedComponent?.message ??
                focusedComponent?.description ??
                'No detail reported'}
            </p>
            <small>
              {focusedIncident
                ? `${focusedIncident.status === 'resolved' ? 'Resolved' : 'Started'} ${relativeTime(
                    focusedIncident.resolvedAt ?? focusedIncident.startedAt,
                  )}`
                : `Observed ${relativeTime(focusedComponent?.observedAt ?? application.observedAt)}`}
            </small>
          </section>
        )}

        <div className="sheet-summary">
          <div>
            <span>Current status</span>
            <StatusPill state={application.status} />
          </div>
          <div>
            <span>24h availability</span>
            <strong>{availability(application.availability24h)}</strong>
          </div>
          <div>
            <span>Last observed</span>
            <strong title={fullDate(application.observedAt)}>
              {relativeTime(application.observedAt)}
            </strong>
          </div>
        </div>

        <ProductMetricsPanel application={application} />

        {application.application.deployment &&
          (application.application.deployment.first ||
            application.application.deployment.latest) && (
            <section className="deployment-profile" aria-label="Deployment history">
              <div className="deployment-profile-head">
                <span>Deployment history</span>
                <small>Provider-verified lifecycle</small>
              </div>
              <div>
                {application.application.deployment.first && (
                  <article>
                    <span>First deployed</span>
                    <strong>
                      {shortDate(application.application.deployment.first.at)}
                    </strong>
                    <time>
                      {fullDate(application.application.deployment.first.at)}
                    </time>
                    <small>
                      {application.application.deployment.first.evidence ??
                        application.application.deployment.first.sources
                          .map((source) => TECHNOLOGY_SOURCE_LABEL[source])
                          .join(' + ')}
                    </small>
                  </article>
                )}
                {application.application.deployment.latest && (
                  <article>
                    <span>Latest deployment</span>
                    <strong>
                      {relativeTime(application.application.deployment.latest.at)}
                    </strong>
                    <time>
                      {fullDate(application.application.deployment.latest.at)}
                    </time>
                    <small>
                      {application.application.deployment.latest.evidence ??
                        application.application.deployment.latest.sources
                          .map((source) => TECHNOLOGY_SOURCE_LABEL[source])
                          .join(' + ')}
                    </small>
                  </article>
                )}
              </div>
            </section>
          )}

        {application.incidents.length > 0 && !focusedIncident && (
          <div className="incident-banner">
            <AlertIcon />
            <div>
              <span>Active incident</span>
              <strong>{application.incidents[0].title}</strong>
              <p>{application.incidents[0].message}</p>
            </div>
          </div>
        )}

        {domainInventory.length > 0 && (
          <section className="routed-domains" aria-label="Managed domains">
            <span>Managed domains</span>
            <div className="routed-domain-primary">
              {featuredDomainInventory.map((domain) => {
                const hosted = domain.sources.some(
                  (source) => source === 'caddy' || source === 'netlify',
                )
                const content = (
                  <>
                    <strong>{domain.hostname}</strong>
                    {hosted && <ExternalIcon />}
                  </>
                )
                return hosted ? (
                  <a
                    href={`https://${domain.hostname}`}
                    target="_blank"
                    rel="noreferrer"
                    key={domain.hostname}
                    title={domain.sources
                      .map((source) => DOMAIN_SOURCE_LABEL[source])
                      .join(' + ')}
                  >
                    {content}
                  </a>
                ) : (
                  <span
                    className="managed-domain"
                    key={domain.hostname}
                    title={domain.sources
                      .map((source) => DOMAIN_SOURCE_LABEL[source])
                      .join(' + ')}
                  >
                    {content}
                  </span>
                )
              })}
            </div>
            {secondaryDomainInventory.length > 0 && (
              <details className="routed-domain-more">
                <summary>
                  <span>
                    {secondaryDomainInventory.length} more{' '}
                    {secondaryDomainInventory.length === 1 ? 'route' : 'routes'}
                  </span>
                  <ChevronIcon />
                </summary>
                <div>
                  {secondaryDomainInventory.map((domain) => {
                    const hosted = domain.sources.some(
                      (source) => source === 'caddy' || source === 'netlify',
                    )
                    const content = (
                      <>
                        <strong>{domain.hostname}</strong>
                        {hosted && <ExternalIcon />}
                      </>
                    )
                    const title = domain.sources
                      .map((source) => DOMAIN_SOURCE_LABEL[source])
                      .join(' + ')
                    return hosted ? (
                      <a
                        href={`https://${domain.hostname}`}
                        target="_blank"
                        rel="noreferrer"
                        key={domain.hostname}
                        title={title}
                      >
                        {content}
                      </a>
                    ) : (
                      <span
                        className="managed-domain"
                        key={domain.hostname}
                        title={title}
                      >
                        {content}
                      </span>
                    )
                  })}
                </div>
              </details>
            )}
          </section>
        )}

        {technologies.length > 0 && (
          <section className="technology-profile" aria-label="Technology and providers">
            <div className="technology-profile-head">
              <div>
                <span>Application profile</span>
                <h3>Stack & providers</h3>
              </div>
              <small>{technologies.length} identified signals</small>
            </div>
            <div className="technology-groups">
              {TECHNOLOGY_CATEGORY_ORDER.map((category) => {
                const items = technologies.filter(
                  (technology) => technology.category === category,
                )
                if (items.length === 0) return null
                return (
                  <div className="technology-group" key={category}>
                    <h4>{TECHNOLOGY_CATEGORY_LABEL[category]}</h4>
                    <div>
                      {items.map((technology) => (
                        <span
                          className={`technology-signal confidence-${technology.confidence}`}
                          key={`${technology.category}-${technology.name}`}
                          title={technologyTitle(technology)}
                        >
                          <strong>{technology.name}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {application.previews.length > 0 && (
          <section className="sheet-section visual-section">
            <div className="section-title">
              <div>
                <span className="section-kicker">Visual checks</span>
                <h3>Rendered happy paths</h3>
              </div>
              <span>
                {application.previews.reduce(
                  (total, preview) => total + preview.frames.length,
                  0,
                )}{' '}
                retained captures
              </span>
            </div>
            <div className="visual-groups">
              {application.previews.map((preview) => (
                <article className="visual-group" key={preview.id}>
                  <header>
                    <div>
                      <div>
                        <h4>{preview.name}</h4>
                        <StatusPill state={preview.status} compact />
                      </div>
                      <a href={preview.url} target="_blank" rel="noreferrer">
                        {preview.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        <ExternalIcon />
                      </a>
                    </div>
                    <time title={fullDate(preview.observedAt)}>
                      Checked {relativeTime(preview.observedAt)}
                    </time>
                  </header>
                  {preview.description && <p>{preview.description}</p>}
                  {preview.frames.length > 0 ? (
                    <div className="preview-gallery">
                      {preview.frames.map((frame, index) => (
                        <button
                          type="button"
                          className="preview-frame"
                          key={frame.id}
                          title={`Preview capture from ${fullDate(frame.capturedAt)}`}
                          onClick={() =>
                            setViewer({
                              previewId: preview.id,
                              frameId: frame.id,
                            })
                          }
                        >
                          <PreviewImage
                            src={frame.imageUrl}
                            alt={`${preview.name} capture from ${fullDate(frame.capturedAt)}`}
                          />
                          <span>
                            <strong>{index === 0 ? 'Latest' : relativeTime(frame.capturedAt)}</strong>
                            <small>{clockTime(frame.capturedAt)}</small>
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="visual-empty">
                      <AlertIcon />
                      <span>{preview.message}</span>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="sheet-section">
          <div className="section-title">
            <div>
              <span className="section-kicker">Subsystems</span>
              <h3>{application.components.length} live checks</h3>
            </div>
            <span>{application.summary}</span>
          </div>
          <div className="component-list">
            {application.components.map((component) => {
              const focused = component.id === focusedComponentId
              return (
              <article
                className={`component-row ${focused ? 'component-row-focused' : ''}`}
                key={component.id}
                data-component-id={component.id}
                aria-label={
                  focused
                    ? `Notification detail: ${component.name}. ${component.message ?? component.description ?? 'No detail reported'}`
                    : undefined
                }
              >
                <StatusDot state={component.status} />
                <div className="component-copy">
                  <div>
                    <h4>{component.name}</h4>
                    {!component.required && <span className="optional-tag">Optional</span>}
                    {focused && <span className="linked-alert-tag">Opened from alert</span>}
                  </div>
                  <p>{component.message ?? component.description ?? 'No detail reported'}</p>
                  <small title={fullDate(component.observedAt)}>
                    Observed {relativeTime(component.observedAt)}
                    {component.lastSuccessAt && ` · Last success ${relativeTime(component.lastSuccessAt)}`}
                  </small>
                </div>
                <div className="component-latency">
                  <strong>{compactLatency(component.latencyMs)}</strong>
                  <span>{component.type}</span>
                </div>
              </article>
              )
            })}
          </div>
        </section>

        <section className="sheet-section">
          <div className="section-title">
            <div>
              <span className="section-kicker">Recent signal</span>
              <h3>Observation history</h3>
            </div>
            <span>{history.length} retained samples</span>
          </div>
          <div className="large-history">
            <HistoryStrip points={history} />
            <div className="history-legend">
              <span><StatusDot state="operational" /> Operational</span>
              <span><StatusDot state="degraded" /> Degraded</span>
              <span><StatusDot state="outage" /> Outage</span>
            </div>
          </div>
        </section>

        <footer className="sheet-actions">
          {application.application.homepage && (
            <a href={application.application.homepage} target="_blank" rel="noreferrer">
              Open application <ExternalIcon />
            </a>
          )}
          {application.application.repository && (
            <a
              className="secondary-link"
              href={application.application.repository}
              target="_blank"
              rel="noreferrer"
            >
              Repository <ExternalIcon />
            </a>
          )}
        </footer>
        </section>
      </div>
      {activePreview && activeFrame && (
        <div
          className="screenshot-viewer"
          role="presentation"
          onMouseDown={() => setViewer(undefined)}
        >
          <section
            className="screenshot-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="screenshot-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <span>{application.application.name} · Visual history</span>
                <h3 id="screenshot-title">{activePreview.name}</h3>
                <small>
                  Capture {activeFrameIndex + 1} of {activePreview.frames.length}
                </small>
              </div>
              <button
                type="button"
                className="icon-button"
                ref={viewerCloseRef}
                onClick={() => setViewer(undefined)}
                aria-label="Close screenshot preview"
              >
                <CloseIcon />
              </button>
            </header>
            <div className="screenshot-stage">
              <button
                type="button"
                className="screenshot-nav screenshot-nav-previous"
                onClick={() => moveViewer(-1)}
                disabled={activeFrameIndex === 0}
                aria-label="Show newer capture"
              >
                <ChevronIcon />
              </button>
              <PreviewImage
                src={activeFrame.imageUrl}
                alt={`${activePreview.name} rendered at ${fullDate(activeFrame.capturedAt)}`}
              />
              <button
                type="button"
                className="screenshot-nav screenshot-nav-next"
                onClick={() => moveViewer(1)}
                disabled={activeFrameIndex === activePreview.frames.length - 1}
                aria-label="Show older capture"
              >
                <ChevronIcon />
              </button>
            </div>
            <div className="screenshot-meta">
              <ScreenshotMetadata
                label="Captured"
                value={fullDate(activeFrame.capturedAt)}
                detail={relativeTime(activeFrame.capturedAt)}
              />
              <ScreenshotMetadata
                label="HTTP"
                value={
                  activeFrame.httpStatus
                    ? `${activeFrame.httpStatus}`
                    : 'Not reported'
                }
                detail={
                  activeFrame.httpStatus && activeFrame.httpStatus < 400
                    ? 'Successful response'
                    : activePreview.message
                }
              />
              <ScreenshotMetadata
                label="Viewport"
                value={`${activeFrame.width} × ${activeFrame.height}`}
                detail="Captured dimensions"
              />
              <ScreenshotMetadata
                label="Page"
                value={activeFrame.pageTitle ?? 'Untitled page'}
                detail={activePreview.url.replace(/^https?:\/\//, '')}
              />
            </div>
            <footer className="screenshot-footer">
              <div className="screenshot-filmstrip" aria-label="Screenshot history">
                {activePreview.frames.map((frame, index) => (
                  <button
                    type="button"
                    className={frame.id === activeFrame.id ? 'active' : ''}
                    key={frame.id}
                    onClick={() =>
                      setViewer({
                        previewId: activePreview.id,
                        frameId: frame.id,
                      })
                    }
                    aria-label={`Show capture ${index + 1} from ${fullDate(frame.capturedAt)}`}
                  >
                    <PreviewImage src={frame.imageUrl} alt="" />
                    <span>{index === 0 ? 'Latest' : relativeTime(frame.capturedAt)}</span>
                  </button>
                ))}
              </div>
              <a href={activePreview.url} target="_blank" rel="noreferrer">
                Open live path <ExternalIcon />
              </a>
            </footer>
          </section>
        </div>
      )}
    </>
  )
}

function ScreenshotMetadata({
  label,
  value,
  detail,
}: {
  label: string
  value: string
  detail: string
}) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  )
}

export function NotificationBadge({
  snapshot,
  onTest,
  testing,
}: {
  snapshot: DashboardSnapshot
  onTest: () => void
  testing: boolean
}) {
  return (
    <button
      type="button"
      className={`notification-badge ${snapshot.notifications.enabled ? 'enabled' : ''}`}
      onClick={onTest}
      disabled={!snapshot.notifications.enabled || testing}
      title={
        snapshot.notifications.enabled
          ? 'Send a test ntfy notification'
          : 'ntfy alerts are not configured'
      }
    >
      <BellIcon />
      <span>{testing ? 'Sending' : snapshot.notifications.enabled ? 'Alerts on' : 'Alerts off'}</span>
    </button>
  )
}
